import { Code2, LogOut, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { components } from '@/lib/api-types-administration';
import { formatDate, formatDateTime } from '@/lib/intl';
import { placeFromRef, samePlace } from '../place/place';
import { GrantRolesPanel } from '../roles/GrantPanels';
import { useRoleText } from '../roles/role-text';
import { Avatar, useExpiryText } from '../ui/person-bits';
import { HoldingItem } from './HoldingItem';
import { useAccessActions } from './person-actions';

export type UserDetail = components['schemas']['UserDetail'];
type Actions = ReturnType<typeof useAccessActions>;
type Place = Parameters<typeof samePlace>[0];
type Kind = 'tenants' | 'organizations';

function PersonHeader({
  person,
  titleId,
}: {
  readonly person: UserDetail;
  readonly titleId: string;
}) {
  const { t } = useTranslation('administration');
  const deactivated = person.status === 'deactivated';
  return (
    <div className="flex items-start gap-3.5">
      <Avatar name={person.displayName} size={48} muted={deactivated} />
      <div className="flex min-w-0 flex-col gap-1">
        <h2 id={titleId} className="text-foreground text-xl leading-tight font-bold">
          {person.displayName}
        </h2>
        <span className="text-muted-foreground text-[13.5px] break-all">{person.email}</span>
        <span className="mt-1">
          <span className={deactivated ? 'status-badge' : 'status-badge success'}>
            {t(`people.status.${person.status}`)}
          </span>
        </span>
      </div>
    </div>
  );
}

function PersonFacts({ person }: { readonly person: UserDetail }) {
  const { t } = useTranslation('administration');
  const direct = person.roles.filter((holding) => holding.via === 'direct').length;
  const inherited = person.roles.length - direct;
  return (
    <dl className="person-facts">
      <div>
        <dt>{t('person.facts.lastSignIn')}</dt>
        <dd>
          {person.lastSignInAt === null
            ? t('person.facts.unknown')
            : formatDateTime(person.lastSignInAt)}
        </dd>
      </div>
      <div>
        <dt>{t('person.facts.updated')}</dt>
        <dd>{formatDate(person.updatedAt)}</dd>
      </div>
      <div>
        <dt>{t('person.facts.rolesHere')}</dt>
        <dd>{t('person.facts.rolesCount', { direct, inherited })}</dd>
      </div>
      <div>
        <dt>{t('person.facts.invitations')}</dt>
        <dd>{person.invitations.length}</dd>
      </div>
    </dl>
  );
}

type HoldingsProps = {
  readonly placeKind: Kind;
  readonly placeId: string;
  readonly currentPlace: Place;
  readonly onChanged: () => void;
  readonly onStanding: () => void;
};

function RolesSection({
  person,
  titleId,
  placeName,
  onGrant,
  ...holdings
}: HoldingsProps & {
  readonly person: UserDetail;
  readonly titleId: string;
  readonly placeName: string;
  readonly onGrant: () => void;
}) {
  const { t } = useTranslation('administration');
  return (
    <section aria-labelledby={`${titleId}-roles`}>
      <div className="flex items-center justify-between gap-3">
        <h3 id={`${titleId}-roles`} className="text-foreground text-[15px] font-semibold">
          {t('person.rolesHeading', { place: placeName })}
        </h3>
        {person.can.grantRoles ? (
          <Button variant="outline" size="sm" onClick={onGrant}>
            <Plus />
            {t('person.grantRole')}
          </Button>
        ) : null}
      </div>
      {person.roles.length === 0 ? (
        <p className="text-muted-foreground mt-2 text-sm">{t('person.noRoles')}</p>
      ) : (
        <ul className="person-holdings">
          {person.roles.map((holding) => (
            <HoldingItem
              key={holding.assignmentId}
              holding={holding}
              personName={person.displayName}
              {...holdings}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function InvitationsSection({
  person,
  titleId,
  actions,
}: {
  readonly person: UserDetail;
  readonly titleId: string;
  readonly actions: Actions;
}) {
  const { t } = useTranslation('administration');
  const { name } = useRoleText();
  const expiry = useExpiryText();
  return (
    <section aria-labelledby={`${titleId}-invitations`}>
      <h3 id={`${titleId}-invitations`} className="text-foreground text-[15px] font-semibold">
        {t('person.invitationsHeading')}
      </h3>
      {person.invitations.length === 0 ? (
        <p className="text-muted-foreground mt-1.5 text-sm">
          {t('person.noInvitations', { name: person.displayName })}
        </p>
      ) : (
        <ul className="mt-2 flex flex-col gap-2">
          {person.invitations.map((invitation) => (
            <li key={invitation.id} className="person-invitation">
              <span className="flex min-w-0 flex-col">
                <span className="text-sm font-semibold">
                  {invitation.roles.map((role) => name(role)).join(', ')}
                </span>
                <span className="text-muted-foreground text-[12.5px]">
                  {expiry(invitation.expiresAt)}
                </span>
              </span>
              {invitation.can.withdraw ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={actions.isPending(`withdraw:${invitation.id}`)}
                  onClick={() => void actions.withdraw(invitation)}
                >
                  {t('people.actions.withdraw')}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function LeaveSection({
  person,
  titleId,
  placeName,
  actions,
}: {
  readonly person: UserDetail;
  readonly titleId: string;
  readonly placeName: string;
  readonly actions: Actions;
}) {
  const { t } = useTranslation('administration');
  return (
    <section aria-labelledby={`${titleId}-leave`} className="person-leave">
      <h3 id={`${titleId}-leave`} className="text-foreground text-sm font-semibold">
        {t('person.leaveHeading')}
      </h3>
      <div className="flex flex-wrap gap-2">
        {person.can.remove ? (
          <Button
            variant="outline"
            className="text-destructive"
            disabled={actions.isPending(`remove:${person.id}`)}
            onClick={() => void actions.remove(person)}
          >
            <LogOut />
            {t('people.actions.remove', { place: placeName })}
          </Button>
        ) : null}
        {person.can.deactivate ? (
          <Button
            variant="outline"
            className="text-destructive"
            disabled={actions.isPending(`deactivate:${person.id}`)}
            onClick={() => void actions.deactivate(person)}
          >
            {t('people.actions.deactivate')}
          </Button>
        ) : null}
        {person.can.reactivate ? (
          <Button
            variant="outline"
            disabled={actions.isPending(`reactivate:${person.id}`)}
            onClick={() => void actions.reactivate(person)}
          >
            {t('people.actions.reactivate')}
          </Button>
        ) : null}
      </div>
      {person.can.remove || person.can.deactivate ? (
        <p className="text-muted-foreground text-[12.5px]">{t('person.leaveText')}</p>
      ) : null}
    </section>
  );
}

function TechnicalDetails({ person }: { readonly person: UserDetail }) {
  const { t } = useTranslation('administration');
  return (
    <details className="person-technical">
      <summary>
        <Code2 aria-hidden="true" className="size-4" />
        {t('common.technicalDetails')}
      </summary>
      <dl>
        <dt>{t('person.technical.userId')}</dt>
        <dd>
          <code>{person.id}</code>
        </dd>
        <dt>{t('person.technical.roleCodes')}</dt>
        <dd>
          <code>{person.roles.map((holding) => holding.role.code).join(', ') || '—'}</code>
        </dd>
      </dl>
    </details>
  );
}

/** The roles this person already holds directly here: the grant panel does not offer them again. */
function heldHereIds(person: UserDetail, currentPlace: Place): Set<string> {
  return new Set(
    person.roles
      .filter((holding) => {
        const held = placeFromRef(holding.place);
        return holding.via === 'direct' && held !== null && samePlace(held, currentPlace);
      })
      .map((holding) => holding.role.id),
  );
}

export function PersonBody({
  person,
  titleId,
  placeKind,
  placeId,
  placeName,
  currentPlace,
  onChanged,
  onStanding,
}: HoldingsProps & {
  readonly person: UserDetail;
  readonly titleId: string;
  readonly placeName: string;
}) {
  const actions = useAccessActions({ placeKind, placeId, placeName, onChanged, onStanding });
  const canLeave = person.can.remove || person.can.deactivate || person.can.reactivate;
  const [granting, setGranting] = useState(false);
  const holdings = { placeKind, placeId, currentPlace, onChanged, onStanding };

  return (
    <div className="flex flex-col gap-5">
      <PersonHeader person={person} titleId={titleId} />
      <PersonFacts person={person} />
      <RolesSection
        person={person}
        titleId={titleId}
        placeName={placeName}
        onGrant={() => setGranting(true)}
        {...holdings}
      />
      <InvitationsSection person={person} titleId={titleId} actions={actions} />
      {canLeave ? (
        <LeaveSection person={person} titleId={titleId} placeName={placeName} actions={actions} />
      ) : null}
      {person.can.grantRoles ? (
        <GrantRolesPanel
          open={granting}
          onOpenChange={setGranting}
          person={person}
          heldRoleIds={heldHereIds(person, currentPlace)}
          onGranted={onChanged}
          onStanding={onStanding}
          placeKind={placeKind}
          placeId={placeId}
          placeName={placeName}
        />
      ) : null}
      <TechnicalDetails person={person} />
    </div>
  );
}
